import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Trash2, Edit, Save, X, ListChecks, FileText, 
  Briefcase, AlertCircle, Loader2, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '../../lib/api';

export default function ListasPage() {
  const [activeTab, setActiveTab] = useState('competencias');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Data states
  const [competencias, setCompetencias] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [cargosContacto, setCargosContacto] = useState([]);
  const [tiposEntidade, setTiposEntidade] = useState([]);
  
  // Edit states
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newItem, setNewItem] = useState('');

  useEffect(() => {
    loadAllLists();
  }, []);

  const loadAllLists = async () => {
    setLoading(true);
    try {
      const [compResp, docsResp, cargosResp, tiposResp] = await Promise.all([
        apiClient.get('/admin/listas/competencias').catch(() => ({ data: [] })),
        apiClient.get('/admin/listas/tipos-documento').catch(() => ({ data: [] })),
        apiClient.get('/admin/listas/cargos-contacto').catch(() => ({ data: [] })),
        apiClient.get('/admin/listas/tipos-entidade').catch(() => ({ data: [] }))
      ]);
      
      setCompetencias(compResp.data || []);
      setTiposDocumento(docsResp.data || []);
      setCargosContacto(cargosResp.data || []);
      setTiposEntidade(tiposResp.data || []);
    } catch (error) {
      console.error('Error loading lists:', error);
      toast.error('Erro ao carregar listas');
    } finally {
      setLoading(false);
    }
  };

  const getListByTab = () => {
    switch (activeTab) {
      case 'competencias': return { data: competencias, setData: setCompetencias, endpoint: 'competencias', title: 'Competências' };
      case 'tipos-documento': return { data: tiposDocumento, setData: setTiposDocumento, endpoint: 'tipos-documento', title: 'Tipos de Documento' };
      case 'cargos': return { data: cargosContacto, setData: setCargosContacto, endpoint: 'cargos-contacto', title: 'Cargos de Contacto' };
      case 'tipos-entidade': return { data: tiposEntidade, setData: setTiposEntidade, endpoint: 'tipos-entidade', title: 'Tipos de Entidade' };
      default: return { data: [], setData: () => {}, endpoint: '', title: '' };
    }
  };

  const handleAdd = async () => {
    if (!newItem.trim()) {
      toast.error('Preencha o nome do item');
      return;
    }
    
    const { endpoint, setData } = getListByTab();
    setSaving(true);
    
    try {
      const response = await apiClient.post(`/admin/listas/${endpoint}`, { nome: newItem.trim() });
      setData(prev => [...prev, response.data]);
      setNewItem('');
      toast.success('Item adicionado com sucesso');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao adicionar item');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    if (!editValue.trim()) {
      toast.error('Preencha o nome do item');
      return;
    }
    
    const { endpoint, setData } = getListByTab();
    setSaving(true);
    
    try {
      await apiClient.put(`/admin/listas/${endpoint}/${id}`, { nome: editValue.trim() });
      setData(prev => prev.map(item => item.id === id ? { ...item, nome: editValue.trim() } : item));
      setEditingId(null);
      setEditValue('');
      toast.success('Item atualizado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Tem a certeza que deseja eliminar este item?')) return;
    
    const { endpoint, setData } = getListByTab();
    
    try {
      await apiClient.delete(`/admin/listas/${endpoint}/${id}`);
      setData(prev => prev.filter(item => item.id !== id));
      toast.success('Item eliminado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar item');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditValue(item.nome);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const renderListItems = () => {
    const { data, title } = getListByTab();
    
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">A carregar...</span>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Add new item */}
        <div className="flex gap-2 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <Input
            placeholder={`Novo ${title.toLowerCase().slice(0, -1)}...`}
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAdd()}
            className="flex-1"
            data-testid="input-new-item"
          />
          <Button onClick={handleAdd} disabled={saving} data-testid="btn-add-item">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Adicionar
          </Button>
        </div>

        {/* List items */}
        <div className="space-y-2">
          {data.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <ListChecks className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Sem itens nesta lista.</p>
            </div>
          ) : (
            data.map((item) => (
              <div 
                key={item.id} 
                className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700"
              >
                {editingId === item.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleEdit(item.id)}
                      className="flex-1"
                      autoFocus
                    />
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(item.id)} disabled={saving}>
                      <Save className="w-4 h-4 text-green-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancelEdit}>
                      <X className="w-4 h-4 text-slate-400" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{item.nome}</span>
                      {item.sistema && (
                        <Badge variant="secondary" className="text-xs">Sistema</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => startEdit(item)}
                        disabled={item.sistema}
                        data-testid={`btn-edit-${item.id}`}
                      >
                        <Edit className="w-4 h-4 text-slate-400" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDelete(item.id)}
                        disabled={item.sistema}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        data-testid={`btn-delete-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
        
        {data.length > 0 && (
          <p className="text-xs text-slate-400 text-right">
            Total: {data.length} {data.length === 1 ? 'item' : 'itens'}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <ListChecks className="w-7 h-7 text-[#0066CC]" />
          Gestão de Listas
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Configure as listas do sistema: competências, tipos de documentos, cargos e tipos de entidade.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="competencias" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Competências</span>
          </TabsTrigger>
          <TabsTrigger value="tipos-documento" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span className="hidden sm:inline">Tipos Documento</span>
          </TabsTrigger>
          <TabsTrigger value="cargos" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" />
            <span className="hidden sm:inline">Cargos</span>
          </TabsTrigger>
          <TabsTrigger value="tipos-entidade" className="flex items-center gap-2">
            <ListChecks className="w-4 h-4" />
            <span className="hidden sm:inline">Tipos Entidade</span>
          </TabsTrigger>
        </TabsList>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{getListByTab().title}</CardTitle>
            <CardDescription>
              {activeTab === 'competencias' && 'Competências disponíveis para voluntários e missões.'}
              {activeTab === 'tipos-documento' && 'Tipos de documentos que podem ser carregados pelas instituições.'}
              {activeTab === 'cargos' && 'Cargos disponíveis para os contactos das instituições.'}
              {activeTab === 'tipos-entidade' && 'Tipos de entidades/formas jurídicas das instituições.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {renderListItems()}
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
